/**
 * Плавающая кнопка-док у края экрана (перенос механики из k24
 * `apps/web/src/lib/use-edge-dock.ts`, образец владельца, без выдумывания
 * заново): тянется по вертикали, за 30 % ширины экрана — перелетает на
 * другой край, место (край + доля высоты дорожки) живёт в localStorage.
 * Перенос не должен считаться нажатием — обычный `onClick` спрашивает у
 * хука `justDragged()`, так кнопка остаётся доступной и с клавиатуры (Tab +
 * Enter не трогают `onPointerDown/Move/Up`).
 *
 * Порт под Preact: React `PointerEvent` из `react` заменён на DOM-тип
 * `PointerEvent` (как в use-drag.ts/use-detent.ts этого проекта), `"use
 * client"` убран — здесь нет RSC.
 */
import { useEffect, useRef, useState } from 'preact/hooks'

export type DockEdge = 'left' | 'right'

export interface EdgeDock {
  edge: DockEdge
  /** 0 — вверху дорожки, 1 — внизу. */
  ratio: number
  /** Идёт перенос: на время можно погасить всплывающие подсказки. */
  dragging: boolean
  /** Последнее взаимодействие было переносом, а не нажатием (сбрасывается). */
  justDragged: () => boolean
  handlers: {
    onPointerDown: (e: PointerEvent) => void
    onPointerMove: (e: PointerEvent) => void
    onPointerUp: (e: PointerEvent) => void
  }
}

/** Читает сохранённое место дока. Экспортирована ради теста «битый JSON». */
export function readDock(key: string): { edge?: DockEdge; ratio?: number } {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as { edge?: unknown; ratio?: unknown }
    const out: { edge?: DockEdge; ratio?: number } = {}
    if (parsed.edge === 'left' || parsed.edge === 'right') out.edge = parsed.edge
    if (typeof parsed.ratio === 'number') out.ratio = parsed.ratio
    return out
  } catch {
    return {} // приватный режим или битая запись — просто дефолт
  }
}

/** 0..1, зажатая доля дорожки — вынесена отдельно ради прямого юнит-теста. */
export function clampRatio(ratio: number): number {
  return Math.min(1, Math.max(0, ratio))
}

/** Новая доля дорожки при переносе на `dyPx` от начала жеста. */
export function dragRatio(startRatio: number, dyPx: number, trackPx: number): number {
  return clampRatio(startRatio + dyPx / Math.max(1, trackPx))
}

/**
 * Перелёт на другой край: `dxPx` — смещение от начала жеста (или от точки
 * последнего перелёта), `screenWidthPx` — ширина экрана. За 30% ширины —
 * кнопка прыгает на противоположный борт; иначе край не меняется (null).
 */
export function flipEdge(edge: DockEdge, dxPx: number, screenWidthPx: number): DockEdge | null {
  const threshold = screenWidthPx * 0.3
  if (edge === 'right' && dxPx < -threshold) return 'left'
  if (edge === 'left' && dxPx > threshold) return 'right'
  return null
}

export function useEdgeDock(opts: {
  storageKey: string
  defaultEdge?: DockEdge
  defaultRatio?: number
}): EdgeDock {
  const { storageKey, defaultEdge = 'right', defaultRatio = 0.7 } = opts
  const [edge, setEdge] = useState<DockEdge>(defaultEdge)
  const [ratio, setRatio] = useState(defaultRatio)
  const [dragging, setDragging] = useState(false)
  const drag = useRef<{
    startX: number
    startY: number
    startRatio: number
    moved: boolean
  } | null>(null)
  const moved = useRef(false)
  const edgeRef = useRef(edge)
  edgeRef.current = edge

  useEffect(() => {
    const saved = readDock(storageKey)
    if (saved.edge) setEdge(saved.edge)
    if (typeof saved.ratio === 'number') setRatio(clampRatio(saved.ratio))
  }, [storageKey])

  const onPointerDown = (e: PointerEvent): void => {
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    drag.current = { startX: e.clientX, startY: e.clientY, startRatio: ratio, moved: false }
  }

  const onPointerMove = (e: PointerEvent): void => {
    const state = drag.current
    if (!state) return
    // Дорожка короче экрана: кнопка не должна упираться в края.
    const track = Math.max(1, window.innerHeight - 200)
    const next = dragRatio(state.startRatio, e.clientY - state.startY, track)
    if (Math.abs(e.clientY - state.startY) > 4) {
      state.moved = true
      setDragging(true)
    }
    setRatio(next)
    const flipped = flipEdge(edgeRef.current, e.clientX - state.startX, window.innerWidth)
    if (flipped) {
      setEdge(flipped)
      state.startX = e.clientX
      state.moved = true
    }
  }

  const onPointerUp = (): void => {
    const state = drag.current
    drag.current = null
    setDragging(false)
    moved.current = state?.moved === true
    if (!moved.current) return // просто нажатие — место не трогаем
    try {
      localStorage.setItem(storageKey, JSON.stringify({ edge: edgeRef.current, ratio }))
    } catch {
      // приватный режим — без сохранения работает так же
    }
  }

  return {
    edge,
    ratio,
    dragging,
    justDragged: () => {
      const was = moved.current
      moved.current = false
      return was
    },
    handlers: { onPointerDown, onPointerMove, onPointerUp },
  }
}
